import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, AuthResponse } from '@/types/auth.types';
import { authService } from '@/services/authService';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      if (typeof window !== 'undefined') {
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setToken(storedToken);
            setUser(parsedUser);
          } catch (error) {
            // Invalid stored data, clear it
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const authResponse = await authService.login(credentials);

      // authService.login already stores token and user in localStorage
      // Update context state to match
      if (authResponse.status === 'success' && authResponse.data) {
        setToken(authResponse.data.token);
        setUser(authResponse.data.user);
      }

      return authResponse;
    } catch (error: any) {
      // Extract error message from axios error response
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message || 
        'Login failed';
      
      // Return error response with specific message
      return {
        status: 'error',
        message: errorMessage,
        data: { token: '', user: { id: '', email: '', name: '', role: '' } }
      };
    }
  };

  const logout = () => {
    // Clear state
    setUser(null);
    setToken(null);

    // Call authService.logout to clear localStorage
    authService.logout();
  };

  const value: AuthContextValue = {
    user,
    token,
    isAuthenticated: user !== null && token !== null,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

